import { useState } from "react";
import { ArrowDownToLine, ArrowUpToLine, PanelLeft, PanelRight, Plus } from "lucide-react";
import { TOPS, addSidelites, addTransom, layout, patchAt, pieceNames, removeUnit, setPieceSize, sliderOf, sliderSec, stackPiece } from "@/lib/design";
import { SlidePicker } from "@/components/quote/SlidePicker";
import { SeriesPicker, ShapePicker } from "@/components/designer/ShapeSeries";
import { FoldPicker, foldsFor } from "@/components/quote/FoldPicker";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const inp = "h-11 w-full rounded-[10px] border border-black/10 bg-white px-3 text-[15px] text-black outline-none focus:border-[#007AFF]";
const base = "inline-flex h-11 items-center justify-center gap-1.5 rounded-full border px-4 text-[14px] font-medium transition-colors disabled:opacity-40";
const btn = `${base} border-black/10 bg-white text-black hover:border-[#007AFF]`;
const primary = `${base} border-[#007AFF] bg-[#007AFF] text-white hover:bg-[#0062CC]`;
const Lab = ({ t, children }) => <label className="block text-[12px] font-semibold uppercase tracking-wide text-[#6D6D72]">{t}<div className="mt-1 normal-case tracking-normal">{children}</div></label>;

const unitOf = (L, path) => [...L.units].reverse().find((u) => path.join().startsWith(u.path.join()) && path.length > u.path.length) || L.units[0];

function SizeInput({ value, testId, onCommit }) {
  return <input key={`${testId}-${value}`} data-testid={testId} inputMode="decimal" defaultValue={Math.round(value * 8) / 8} className={inp}
    onBlur={(e) => { const v = Number(e.target.value); if (v !== value && onCommit(v) === false) e.target.value = Math.round(value * 8) / 8; }}
    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />;
}

// Selected piece: exact width/height (totals follow), top shape, remove.
export function SelectedPiece({ d, path, set, onMsg, price }) {
  const [note, setNote] = useState("");
  const L = layout(d);
  const u = unitOf(L, path);
  const name = pieceNames(d)[L.units.indexOf(u)];
  const sl = sliderOf(u.node);
  const commit = (dim) => (v) => {
    if (!(v >= 12)) { onMsg("Minimum piece size 12 in"); return false; }
    set(setPieceSize(d, u.path, dim, v));
  };
  return (
    <div className="space-y-3 rounded-[14px] bg-white p-4" data-testid="design-piece-panel">
      <p className="text-[17px] font-semibold text-black" data-testid="design-piece-name">{name}</p>
      <div className="grid grid-cols-2 gap-2">
        <Lab t="Width (in)"><SizeInput value={u.w} testId="piece-width-input" onCommit={commit("w")} /></Lab>
        <Lab t="Height (in)"><SizeInput value={u.h} testId="piece-height-input" onCommit={commit("h")} /></Lab>
        <Lab t="Top shape">
          <select data-testid="piece-top-select" value={u.node.top || "rect"} onChange={(e) => set(patchAt(d, u.path, { top: e.target.value }))} className={inp}>
            {TOPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Lab>
        <Lab t="Bi-fold panels">
          <select data-testid="piece-bifold-panels" value={u.node.bifold?.panels || ""} className={inp}
            onChange={(e) => {
              const k = Number(e.target.value), old = u.node.bifold?.fold, fold = k && foldsFor(k).includes(old) ? old : `${k}L`;
              setNote(k && old && fold !== old ? `Fold reset to ${fold}: ${old} doesn't fit ${k} panels` : "");
              set(patchAt(d, u.path, { bifold: k ? { panels: k, fold, smart_lock: !!u.node.bifold?.smart_lock } : null }));
            }}>
            <option value="">Not bi-fold</option>
            {[2, 3, 4, 5, 6, 7, 8].map((k) => <option key={k} value={k}>{k} panels</option>)}
          </select>
        </Lab>
        {u.node.bifold && (
          <FoldPicker inline panels={u.node.bifold.panels} value={u.node.bifold.fold} w={u.w} h={u.h} testId="piece-fold-picker"
            onChange={(f) => { setNote(""); set(patchAt(d, u.path, { bifold: { ...u.node.bifold, fold: f } })); }} />
        )}
        {u.node.bifold && note && <p data-testid="piece-fold-note" className="col-span-2 text-xs font-semibold text-[#C93400]">{note}</p>}
        {u.node.bifold && (
          <label className="col-span-2 flex items-center gap-2 text-[15px] text-black">
            <input type="checkbox" data-testid="piece-bifold-smart-lock" checked={!!u.node.bifold.smart_lock} onChange={(e) => set(patchAt(d, u.path, { bifold: { ...u.node.bifold, smart_lock: e.target.checked } }))} /> Smart lock
          </label>
        )}
        {L.units.length > 1 && <div className="flex items-end"><button data-testid="remove-piece-button" onClick={() => set(removeUnit(d, u.path))} className={`${btn} w-full text-[#D70015]`}>Remove piece</button></div>}
        <SeriesPicker d={d} u={u} set={set} price={price} />
        {!u.node.bifold && !sl && <ShapePicker d={d} u={u} set={set} />}
        {sl && (
          <SlidePicker inline kind={u.node.kind === "door" ? "sliding_patio" : "sliding"} config={sl.config} dirs={sl.dirs} widths={sl.widths} w={u.w}
            testId="piece-slide-picker" onChange={(p) => {
              const cfg = p.config ?? sl.config, dirs = p.slide_dirs ?? (p.config ? "" : sl.dirs);
              const widths = p.panel_widths ?? (p.config && p.config.length !== sl.config.length ? [] : sl.widths);
              set(patchAt(d, u.path, { sec: sliderSec(cfg, dirs, widths, u.w, sl.leaves), slider: true }));
            }} />
        )}
      </div>
    </div>
  );
}

// Add-on pieces: each opens a sheet; nothing changes until "Add". Cancel, Esc or tapping outside leaves the design as it was.
const FLOWS = [["sidelite", "Sidelite", PanelLeft], ["transom", "Transom", Plus], ["stack", "Stacked unit", ArrowUpToLine]];
const DRAFT = { sw: 14, side: "left", th: 18, top: "rect", st: { side: "top", size: 24, kind: "window" } };

export function AddPieces({ d, set }) {
  const [flow, setFlow] = useState(null);
  const [f, setF] = useState(DRAFT);
  const n = (v) => Math.max(12, Number(v) || 12);
  const open = (k) => { setF(DRAFT); setFlow(k); };
  const add = () => {
    set(flow === "sidelite" ? addSidelites(d, f.side, n(f.sw)) : flow === "transom" ? addTransom(d, n(f.th), f.top) : stackPiece(d, f.st.side, n(f.st.size), f.st.kind));
    setFlow(null);
  };
  return (
    <div className="space-y-3 rounded-[14px] bg-white p-4" data-testid="design-add-pieces">
      <p className="text-[17px] font-semibold text-black">Add pieces</p>
      <div className="grid grid-cols-3 gap-2">
        {FLOWS.map(([k, l, I]) => <button key={k} data-testid={`open-add-${k}`} onClick={() => open(k)} className={btn}><I className="h-4 w-4" /> {l}</button>)}
      </div>
      <Dialog open={!!flow} onOpenChange={(o) => !o && setFlow(null)}>
        <DialogContent data-testid="add-piece-sheet" className="max-w-md rounded-[18px] border-0 bg-[#F2F2F7] p-5 text-black">
          <DialogTitle className="text-[17px] font-semibold">Add {FLOWS.find(([k]) => k === flow)?.[1].toLowerCase()}</DialogTitle>
          <div className="space-y-3 rounded-[14px] bg-white p-4">
            {flow === "sidelite" && (<>
              <Lab t="Sidelite width (in)"><input data-testid="sidelite-width-input" inputMode="decimal" value={f.sw} onChange={(e) => setF({ ...f, sw: e.target.value })} className={inp} /></Lab>
              <div className="grid grid-cols-3 gap-2" role="radiogroup">
                {[["left", "Left", PanelLeft], ["right", "Right", PanelRight], ["both", "Both", null]].map(([k, l, I]) => (
                  <button key={k} data-testid={`add-sidelite-${k}`} role="radio" aria-checked={f.side === k} onClick={() => setF({ ...f, side: k })}
                    className={f.side === k ? primary : btn}>{I && <I className="h-4 w-4" />} {l}</button>
                ))}
              </div>
            </>)}
            {flow === "transom" && (<div className="grid grid-cols-2 gap-2">
              <Lab t="Transom height"><input data-testid="transom-height-input" inputMode="decimal" value={f.th} onChange={(e) => setF({ ...f, th: e.target.value })} className={inp} /></Lab>
              <Lab t="Shape"><select data-testid="transom-shape-select" value={f.top} onChange={(e) => setF({ ...f, top: e.target.value })} className={inp}>{TOPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
            </div>)}
            {flow === "stack" && (<div className="grid grid-cols-3 gap-2">
              <Lab t="Position"><select data-testid="stack-side-select" value={f.st.side} onChange={(e) => setF({ ...f, st: { ...f.st, side: e.target.value } })} className={inp}>
                {[["top", "Above"], ["bottom", "Below"], ["left", "Left"], ["right", "Right"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
              <Lab t="Size (in)"><input data-testid="stack-size-input" inputMode="decimal" value={f.st.size} onChange={(e) => setF({ ...f, st: { ...f.st, size: e.target.value } })} className={inp} /></Lab>
              <Lab t="Kind"><select data-testid="stack-kind-select" value={f.st.kind} onChange={(e) => setF({ ...f, st: { ...f.st, kind: e.target.value } })} className={inp}>
                <option value="window">Window</option><option value="door">Door</option></select></Lab>
            </div>)}
          </div>
          {[flow === "sidelite" && f.sw, flow === "transom" && f.th, flow === "stack" && f.st.size].some((v) => v !== false && !(Number(v) >= 12)) && (
            <p data-testid="add-piece-min-note" className="text-xs font-semibold text-[#C93400]">Minimum 12 in — Add will use 12 in</p>)}
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="add-piece-cancel" onClick={() => setFlow(null)} className={btn}>Cancel</button>
            <button data-testid="add-piece-confirm" onClick={add} className={primary}>
              {flow === "stack" && f.st.side === "bottom" ? <ArrowDownToLine className="h-4 w-4" /> : <Plus className="h-4 w-4" />} Add</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
